'use strict'

// JavaScript files stored on application settings and run by the live app.
// Order is the array order. A file that fails does not stop the later files.

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/
const MAX_FILES = 20
const MAX_NAME_LENGTH = 64
const MAX_CODE_LENGTH = 100000

function isPlainObject (value) {

  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function isIdentifier (value) {

  return typeof value === 'string' && IDENTIFIER.test(value)
}

function cleanName (value) {

  return String(value || '').trim()
}

function nameProblem (name, usedNames) {

  if (!name) return 'Each custom code file needs a name.'
  if (name.length > MAX_NAME_LENGTH) return 'A custom code file name is too long.'
  if (/[\\/\0]/.test(name)) return 'A custom code file name cannot include a slash.'
  if (usedNames.has(name)) return 'Custom code file names have to be different.'

  return ''
}

function uniqueTitleFromName (name, used) {

  let base = cleanName(name).replace(/\.js$/i, '')
  base = base.replace(/[^A-Za-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')

  if (!base || !/^[A-Za-z_]/.test(base)) base = `file_${base}`.replace(/_+$/g, '')
  if (!/^[A-Za-z_]/.test(base)) base = 'file'

  base = base.slice(0, 40)

  let title = base
  let n = 2

  while (used.has(title) || !isIdentifier(title)) {

    const suffix = `_${n}`

    title = `${base.slice(0, Math.max(1, 40 - suffix.length))}${suffix}`
    n += 1
  }

  used.add(title)

  return title
}

function normalizeFile (raw, usedIds, usedNames, repair) {

  if (!isPlainObject(raw)) return { error: 'A custom code file must be an object.' }

  const name = cleanName(raw.name)
  const problem = nameProblem(name, usedNames)

  if (problem) return repair ? null : { error: problem }

  const code = raw.code == null ? '' : raw.code

  if (typeof code !== 'string') return repair ? null : { error: 'Custom code must be text.' }
  if (code.length > MAX_CODE_LENGTH) return repair ? null : { error: 'A custom code file is too long.' }

  let uniqueTitle = cleanName(raw.uniqueTitle)

  if (!isIdentifier(uniqueTitle) || usedIds.has(uniqueTitle)) {

    if (!repair) {

      if (!isIdentifier(uniqueTitle) && uniqueTitle) return { error: 'A custom code file id must start with a letter or underscore.' }
      if (usedIds.has(uniqueTitle)) return { error: 'Custom code file ids have to be different.' }

      uniqueTitle = ''
    }

    if (!uniqueTitle || !isIdentifier(uniqueTitle) || usedIds.has(uniqueTitle)) {

      uniqueTitle = uniqueTitleFromName(name, usedIds)
    }
  }

  usedIds.add(uniqueTitle)
  usedNames.add(name)

  return {
    file: {
      uniqueTitle,
      name,
      code
    }
  }
}

function normalizeCustomCode (value) {

  if (value == null) return { customCode: { files: [] } }
  if (!isPlainObject(value)) return { error: 'customCode must be an object.' }
  if (value.files != null && !Array.isArray(value.files)) return { error: 'customCode.files must be a list.' }

  const source = Array.isArray(value.files) ? value.files : []

  if (source.length > MAX_FILES) return { error: `Use at most ${MAX_FILES} custom code files.` }

  const usedIds = new Set()
  const usedNames = new Set()
  const files = []

  for (const raw of source) {

    const normalized = normalizeFile(raw, usedIds, usedNames, false)

    if (normalized.error) return normalized

    files.push(normalized.file)
  }

  return { customCode: { files } }
}

function customCodeForRead (value) {

  if (!isPlainObject(value) || !Array.isArray(value.files)) return { files: [] }

  const usedIds = new Set()
  const usedNames = new Set()
  const files = []

  for (const raw of value.files) {

    if (files.length >= MAX_FILES) break

    const normalized = normalizeFile(raw, usedIds, usedNames, true)

    if (!normalized || !normalized.file) continue
    if (normalized.file.code.length > MAX_CODE_LENGTH) continue

    files.push(normalized.file)
  }

  return { files }
}

// The record a view link script reads as turbohammerRow.
// The view passes the stored row on item._values. Formatting, links,
// filenames, and file types stay off the object the script sees.
function customCodeRow (item) {

  const source = item && item._values && typeof item._values === 'object' ? item._values : {}
  const row = {}

  for (const key of Object.keys(source)) {

    if (key === '_formatting' || key === '_links' || key === 'links') continue
    if (key.endsWith('_filename') || key.endsWith('_type')) continue

    row[key] = source[key]
  }

  return row
}

function blankCustomCodeFile (files) {

  const existing = customCodeForRead({ files: Array.isArray(files) ? files : [] }).files
  const usedNames = new Set(existing.map((file) => file.name))
  let name = 'script.js'
  let n = 2

  while (usedNames.has(name)) {

    name = `script_${n}.js`
    n += 1
  }

  const usedIds = new Set(existing.map((file) => file.uniqueTitle))

  return {
    uniqueTitle: uniqueTitleFromName(name, usedIds),
    name,
    code: ''
  }
}

async function runCustomCode (customCode, evaluate) {

  const files = customCodeForRead(customCode).files
  const failures = []

  if (typeof evaluate !== 'function') return failures

  for (const file of files) {

    if (!String(file.code || '').trim()) continue

    try {

      await Promise.resolve(evaluate(file))
    } catch (error) {

      failures.push({
        uniqueTitle: file.uniqueTitle,
        name: file.name,
        message: error && error.message ? String(error.message) : 'failed'
      })

      if (typeof console !== 'undefined' && console.error) {

        console.error(`Custom code ${file.name} failed and was skipped.`, error)
      }
    }
  }

  return failures
}

module.exports = {
  MAX_FILES,
  MAX_CODE_LENGTH,
  MAX_NAME_LENGTH,
  blankCustomCodeFile,
  customCodeForRead,
  customCodeRow,
  normalizeCustomCode,
  runCustomCode,
  uniqueTitleFromName
}
